import React, { useState } from "react";
import "./EntryPage.css";
import { useNavigate } from "react-router-dom";

import { collection, doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "./firebase";

function EntryPage({ onStart, onFaultEntry }) {
  const [latLng, setLatLng] = useState("");
  const [error, setError] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);
  const [faultDate, setFaultDate] = useState("");
  const [isMapDragOver, setIsMapDragOver] = useState(false);
  const [isFaultDragOver, setIsFaultDragOver] = useState(false);

  const [loadedData, setLoadedData] = useState(null);
  const navigate = useNavigate();
  const [uploadStatus, setUploadStatus] = useState("");

  const handleLatLngChange = (e) => {
    setLatLng(e.target.value);
    setError("");
  };

  const handleFaultDateChange = (e) => {
    setFaultDate(e.target.value);
    setError("");
  };

  const handleCreateNewMap = () => {
    const [lat, lng] = latLng.split(",").map((val) => parseFloat(val.trim()));
    if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      onStart({lat, lng});
      navigate("/map", { state: { lat, lng } });
    } else {
      setError("Invalid latitude and longitude. Use format: lat, lng");
    }
  };

  const handleMapDragOver = (e) => {
    e.preventDefault();
    setIsMapDragOver(true);
  };

  const handleMapDragLeave = () => {
    setIsMapDragOver(false);
  };

  const handleFaultDragOver = (e) => {
    e.preventDefault();
    setIsFaultDragOver(true);
  };

  const handleFaultDragLeave = () => {
    setIsFaultDragOver(false);
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];

    if (
      file &&
      (file.type === "application/json" ||
        file.name.endsWith(".json") ||
        file.name.endsWith(".geojson"))
    ) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const geoJson = JSON.parse(e.target.result);
          if (
            geoJson.type === "FeatureCollection" &&
            Array.isArray(geoJson.features) &&
            geoJson.features.length > 0 &&
            geoJson.features[0].geometry &&
            geoJson.features[0].geometry.coordinates
          ) {
            const firstCoordinates = geoJson.features[0].geometry.coordinates[0][0];
            if (Array.isArray(firstCoordinates) && firstCoordinates.length === 2) {
              const [lng, lat] = firstCoordinates;
              if (!isNaN(lat) && !isNaN(lng)) {
                onStart({ geoJson, lat, lng });
                navigate("/map", { state: { geoJson, lat, lng } });
                setError(""); // Clear errors
                alert("GeoJSON file loaded successfully!");
              } else {
                setError("Invalid coordinates in the first feature.");
              }
            } else {
              setError("Invalid coordinate structure in the GeoJSON file.");
            }
          } else {
            setError("Invalid GeoJSON structure. Expected a FeatureCollection with valid geometry.");
          }
        } catch (error) {
          setError("Error parsing the GeoJSON file. Ensure it is valid JSON.");
        }
      };
      reader.readAsText(file);
    } else {
      setError("Please upload a valid .json or .geojson file with a GeoJSON structure.");
    }
  };

  const handleFaultFileDrop = (e) => {
    e.preventDefault();
    setIsFaultDragOver(false);
    const file = e.dataTransfer.files[0];

    if (file && (file.type === "application/json" || file.name.endsWith(".json"))) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const parsedData = JSON.parse(e.target.result);

          if (parsedData.date && Array.isArray(parsedData.faults)) {
            alert("Work-in-progress JSON uploaded successfully!");
            setLoadedData(parsedData.faults);
            setFaultDate(parsedData.date);

            onFaultEntry(parsedData.date, parsedData.faults);
          } else {
            setError("Invalid JSON structure. Ensure it has a 'date' field and a 'faults' array.");
          }
        } catch (error) {
          setError("Error parsing the JSON file. Ensure it is valid JSON.");
        }
      };
      reader.readAsText(file);
    } else {
      setError("Please upload a valid Fault JSON file with a .json extension.");
    }
  };

  const databaseFaultFileDrop = (e) => {
    e.preventDefault();
    setIsFaultDragOver(false);
    const file = e.dataTransfer.files[0];

    if (file && (file.type === "application/json" || file.name.endsWith(".json"))) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const parsedData = JSON.parse(e.target.result);

          if (parsedData.date && Array.isArray(parsedData.faults)) {
            alert("Fault JSON file loaded successfully!");
            setLoadedData(parsedData); // Save entire JSON structure, including date
          } else {
            setError("Invalid JSON structure. Ensure it has a 'date' field and a 'faults' array.");
          }
        } catch (error) {
          setError("Error parsing the JSON file. Ensure it is valid JSON.");
        }
      };
      reader.readAsText(file);
    } else {
      setError("Please upload a valid Fault JSON file with a .json extension.");
    }
  };

  const uploadFaultDataToDatabase = async () => {
    if (!siteName.trim()) {
      setError("Please enter a site name.");
      return;
    }

    if (!loadedData || !loadedData.date || !Array.isArray(loadedData.faults) || loadedData.faults.length === 0) {
      setError("No valid fault data loaded. Please load a Fault JSON file.");
      return;
    }

    setUploadStatus("Validating site...");
    try {
      const siteDocRef = doc(db, "sites", siteName.trim());
      const siteDoc = await getDoc(siteDocRef);

      if (!siteDoc.exists()) {
        setError(`Site "${siteName}" does not exist.`);
        setUploadStatus("");
        return;
      }

      setUploadStatus("Uploading data...");

      const dateID = loadedData.date;
      const dateDocRef = doc(siteDocRef, "dates", dateID);
      const faultsCollectionRef = collection(dateDocRef, "faults");

      const batchPromises = loadedData.faults.map(async (fault) => {
        return setDoc(doc(faultsCollectionRef), {  
          ID: fault.ID, 
          location: fault.location,
          category: fault.category,
          level: fault.level,
          notes: fault.notes,
          latitude: fault.latitude,
          longitude: fault.longitude,
        });
      });

      await Promise.all(batchPromises);

      setUploadStatus("Upload successful!");
    } catch (error) {
      console.error("Error uploading fault data to Firestore:", error);
      setError("Failed to upload fault data. Please try again.");
      setUploadStatus("");
    }
  };

  const [siteName, setSiteName] = useState("");
  const handleSiteNameChange = (e) => {
    setSiteName(e.target.value);
    setError("");
  };

  const handleFaultEntry = () => {
    if (/^\d{8}$/.test(faultDate)) {
      navigate("/fault-entry", { state: { faultDate } }); // Navigate to FaultEntry page
    } else {
      setError("Please enter a valid date in DDMMYYYY format.");
    }
  };

  return (
    <div className="entry-page">
      <h1>Horizon View's Map Generator</h1>

      <div className="entry-container">

        {/*Left Square */}
        <div className="entry-box">
          <h2>Map Drawing</h2>
          <p>Enter a starting location or load an existing map.</p>
          <div className="input-container">
            <input
              type="text"
              placeholder="Enter Latitude, Longitude"
              value={latLng}
              onChange={handleLatLngChange}
            />
            <button className="entry-button" onClick={handleCreateNewMap}>Create New Map</button>
          </div>
          <div>
            <p>Or</p>
          </div>
          <div
            className={`drag-drop-area ${isDragOver ? "drag-over" : ""}`}
            onDragOver={handleMapDragOver}
            onDragLeave={handleMapDragLeave}
            onDrop={handleFileDrop}
          >
            Drag and drop a valid map file here
          </div>
        </div>

        {/*right square*/}
        <div className="entry-box">
          <h2>Fault Entry</h2>
          <p>Enter analysis date for fault entry</p>
          <div className="input-container">
            <input
              type="text"
              placeholder="Enter date (DD/MM/YYYY)"
              value={faultDate}
              onChange={handleFaultDateChange}
            />
            <button
              className="entry-button"
              onClick={handleFaultEntry}
            >
              Start Fault Entry
            </button>
          </div>
          <div>
            <p>Or</p>
          </div>
          <div
            className={`drag-drop-area ${isFaultDragOver ? "drag-over" : ""}`}
            onDragOver={handleFaultDragOver}
            onDragLeave={handleFaultDragLeave}
            onDrop={handleFaultFileDrop}
          >
            Drag and drop a Fault JSON file here
          </div>

        </div>

      </div>

      {/* Fault Entry */}
      <div className="database-entry">
        <h2>Database Upload</h2>
        <p>Enter the site name for fault data upload</p>
        <div className="input-container">
          <input
            type="text"
            placeholder="Enter Site Name"
            value={siteName}
            onChange={handleSiteNameChange}
          />
        </div>
        <div>
          <p>And</p>
        </div>
        <div
          className={`drag-drop-area ${isFaultDragOver ? "drag-over" : ""}`}
          onDragOver={(e) => e.preventDefault() || setIsFaultDragOver(true)}
          onDragLeave={() => setIsFaultDragOver(false)}
          onDrop={databaseFaultFileDrop}
        >
          Drag and drop a Fault JSON file here
        </div>
        {/* Upload Button */}
        <div className="upload-container">
          <button
            className="upload-button"
            onClick={uploadFaultDataToDatabase}
          >
            Upload to Database
          </button>
        </div>
        {uploadStatus && <p className="upload-status">{uploadStatus}</p>}
        {error && <p className="error-message">{error}</p>}
      </div>

    </div >
  );
}

export default EntryPage;